// Angular imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RXJS imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Constant imports
import { CacheDuration } from '../../../_constants/cache-duration';
import { URLConstants } from '../../../_constants/url-constants';

// Service imports
import { MPCacheRegistrationService } from '../../../_services/shared-services/mp-cache-registration-service';

// Model imports
import { FileInfoDisplayResultDto } from '../../../_models/file-info-display-result-dto';
import { VendorContractBaseParameterDto } from '../_models/vendor-contract-base-parameter-dto';
import { CommunityVendorBaseParametersDto } from '../_models/community-vendor-base-parameters-dto';
import { DownloadVendorContractDocumentParameterDto } from '../_models/download-vendor-contract-document-parameter-dto';
import { VendorContractHistoryResultDto } from '../_models/vendor-contract-history-result-dto';
import { DeleteVendorContractParameterDto } from '../_models/delete-vendor-contract-parameter-dto';

@Injectable({
    providedIn: 'root'
})
export class VendorContractService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    deleteContract(baseParam: DeleteVendorContractParameterDto): Observable<any> {
        const url = `${URLConstants.ccCommunityVendorBaseApiUrl}/contract`;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(baseParam)
        };
        return this.httpClient.delete(url, options);
    }

    downloadContractDocument(params: DownloadVendorContractDocumentParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccCommunityVendorBaseApiUrl}/contract/document/download${params.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getContractDocument(params: VendorContractBaseParameterDto): Observable<FileInfoDisplayResultDto[]> {
        const url = `${URLConstants.ccCommunityVendorBaseApiUrl}/contract/documents${params.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, params.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any[]) => plainToClass(FileInfoDisplayResultDto, responseData)));
    }

    getContractHistory(params: VendorContractBaseParameterDto): Observable<VendorContractHistoryResultDto[]> {
        const url = `${URLConstants.ccCommunityVendorBaseApiUrl}/contracts/history${params.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, params.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any[]) => plainToClass(VendorContractHistoryResultDto, responseData)));
    }

    getContractAttachedDocument(params: CommunityVendorBaseParametersDto): Observable<FileInfoDisplayResultDto[]> {
        const url = `${
            URLConstants.ccCommunityVendorBaseApiUrl
        }/contract/document/attach/list${params.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any[]) => plainToClass(FileInfoDisplayResultDto, responseData)));
    }
}
